import React, { useState } from 'react';
import OrderDetailsModal from 'components/OrderDetailsModal';
import DealEnum from 'model/DealEnum';
import AccountContext from 'storage/AccountContext';
import OrdersContext from 'storage/OrdersContext';
import { createMultiSortClickHandler, getSortIndicator, backendFormatToMultiSort, type SortField } from 'utils/sorting';
import OrdersViewRow from './OrdersViewRow';

const headers = [
  { field: 'view', title: '', sortable: false },
  { field: 'createdDate', title: 'Request Date', sortable: true },
  { field: 'status', title: 'Request Status', sortable: true },
  { field: 'author', title: 'RCA Name', sortable: true },
  { field: 'journalName', title: 'Journal', sortable: true },
  { field: 'allocatedAmount', title: 'Amount', sortable: true },
  { field: 'revenueModel', title: 'Revenue Model', sortable: true },
  { field: 'article.woadPublicationStatus', title: 'Publication Status', sortable: true },
];

type Props = {
  orders: WoaRequest[];
  pageSize?: number;
  selectedAccount: WoaAccount;
  sortable?: boolean;
  totalOrders?: number;
  updateOrdersList: () => void;
};

function OrdersList({
  orders,
  pageSize = 15,
  selectedAccount,
  sortable = true,
  totalOrders = 0,
  updateOrdersList,
}: Props) {
  const [viewDetailsOrder, setViewDetailsOrder] = useState<number | null>(null);
  const { sortBy, sortDirection, updateSorting } = OrdersContext.useContext();

  const currentSortFields: SortField[] = backendFormatToMultiSort(sortBy, sortDirection);

  const closeDetails = () => setViewDetailsOrder(null);

  const openDetails = (order: WoaRequest) => setViewDetailsOrder(order.id);

  const onClickSort = createMultiSortClickHandler(headers, currentSortFields, updateSorting);

  return (
    <div>
      {!!viewDetailsOrder && (
        <OrderDetailsModal close={closeDetails} orderId={viewDetailsOrder} showPrice={selectedAccount.showPrice} />
      )}
      <table className="table ordersView">
        <thead>
          <tr className="ordersView-headRow" style={{ textAlign: 'center' }}>
            {headers
              .filter(header => header.field !== 'revenueModel' || selectedAccount.dealType === DealEnum.BOTH)
              .map(header => {
                const { direction, order } = getSortIndicator(header.field, currentSortFields);

                return (
                  <th key={header.field} className="ordersView-headCell">
                    {sortable && header.sortable ? (
                      <button
                        className="btn btn-link text-reset p_0 bold"
                        data-field={header.field}
                        data-seleniumid={`ordersView-sort--${header.field}`}
                        onClick={onClickSort}
                        style={{ fontSize: '1em' }}
                        title="Click to sort: asc -> desc -> none"
                        type="button"
                      >
                        {header.title}
                        {direction && (
                          <>
                            <span className={direction === 'asc' ? 'm--sortAsc' : 'm--sortDesc'} />
                            {order && <small className="ml-1">{order}</small>}
                          </>
                        )}
                      </button>
                    ) : (
                      header.title
                    )}
                  </th>
                );
              })}
          </tr>
        </thead>
        <tbody>
          {orders.map(order =>
            pageSize === 0 ? null : (
              <OrdersViewRow
                key={order.id}
                currency={selectedAccount.currency}
                dealType={selectedAccount.dealType}
                onView={openDetails}
                order={order as WoaRequest & { status: string; woaaccount: WoaAccount }}
                showPrice={selectedAccount.showPrice}
              />
            ),
          )}
        </tbody>
      </table>

      <div>
        {/* It is KOSTYL for react bug. Ask Denis Shelest about it */}
        {orders.length === 0 ? 'No request found' : ''}
      </div>
      {orders.length > 0 && orders.length < totalOrders && (
        <button
          className="btn btn-secondary btn-block mt_10"
          data-seleniumid="ordersView-seeMore"
          onClick={updateOrdersList}
          type="button"
        >
          {`See More (${pageSize})`}
        </button>
      )}
    </div>
  );
}

export default AccountContext.withContext(['selectedAccount'])(OrdersList);
